import type * as PIXI from 'pixi.js';
import { Container as PixiContainer, Rectangle } from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { OpaqueAsset } from '../../video-data/types';
import type { MorphableProperty } from '../morph/MorphManager';
import { assertAssetName, assertBoolean, assertInArray, assertInEnum } from '../../validators';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import { CircleMask } from '../mask/CircleMask';
import { PolygonMask } from '../mask/PolygonMask';
import { Config } from '../../video-data/config';

import {
    BlurFilter,
    AdjustmentFilter,
    ColorChannelMapFilter,
    DuoToneFilter,
    GreyscaleFilter,
    HslAdjustmentFilter,
    OldFilmFilter,
} from '../filters';
import type { Filter } from '../filters/Filter';
import type {
    Asset,
    AssetTemplateReferenceMap,
    DestroyOptions,
    FetchResources,
    Interpolation,
    Transformation as TransformationType,
} from '../../types';

import { Transformation } from '../Transformation';
import { BooleanAttribute, ColorAttribute, getAllTemplateAttributes } from '../attributes';
import { getTemplateValueAttribute, objectHasTemplateValues } from '../attributes/template/common';
import { AssetAnchorDisplay, AssetBoundsDisplay, AssetMaxBoundsDisplay } from '../helper';
import { rgb2int } from '../../libs/videoData';
import type { BufferingMode, SeekMode, AssetCreatedFrom } from '../../enums';
import { ValueMapMode } from '../../enums';
import type { LoadSettings, SerializableMixinInterface } from '../serializableMixin';
import type { SceneGroup } from '../SceneGroup';
import type { Slide } from '../slide/Slide';
import type { Subslide } from '../slide/Subslide';
import { StencilMask } from '../mask/StencilMask';
import { ColorGradingFilter } from '../filters/ColorGradingFilter';
import type { RemoveAnchoredPoints } from '../../libs/Curve';
import type { ContainerSettings } from './Container';
import { Container } from './Container';
import { findTemplateIndexErrors, getStylePropertiesToCopy } from './utils';

type GetMaskType<T extends MaskType> = T extends typeof Config.CIRCLE_MASK_TYPE
    ? CircleMask
    : T extends typeof Config.POLYGON_MASK_TYPE
      ? PolygonMask
      : never;
type MaskType = typeof Config.CIRCLE_MASK_TYPE | typeof Config.POLYGON_MASK_TYPE;

export type MorphParameters = {
    startPoint: number;
    interpolation: Interpolation;
    properties?: string[];
};

enum AssetMode {
    /**
     * Set initial values
     */
    BUILD = 'BUILD',
    /**
     * Evaluate values
     */
    EVAL = 'EVAL',
}

const ASSET_NAME_REGEX = /(.*)_(\d+)(_.*)?/;

export type BaseAssetSettings = {
    /**
     * if updates should bubble upward
     * @default false
     */
    isFinalTopLevel?: boolean;
    /**
     * if drawing should happen automatically
     * @default false
     */
    autoDraw?: boolean;
    /**
     * triggers a redraw if true
     * @default true
     */
    requiresRedraw?: boolean;
    /**
     * redraw events should bubble upward
     * @default false
     */
    updateParentOnRedraw?: boolean;
    /**
     * redraw events should bubble upward (through luminaryParent)
     * @default false
     */
    updateLuminaryParentOnRedraw?: boolean;
    /**
     * object on which to emit update events
     * @default this
     */
    updateEventSource?: UpdateEventSource;
    /**
     * prevents assets from loading if false. A boolean will set all options to that value
     */
    fetchResources: FetchResources;
    /**
     * enables the boundsWithoutAnimation api
     * It's an expensive api to support for large & deep trees (like captions right now),
     * so we're making it opt-in for the use cases we need for now.
     * @default false
     */
    enableBoundsWithoutAnimationForSubtree?: boolean;
    /**
     * how the asset was created
     * @default AssetCreatedFrom.SCENE_DESIGN
     */
    createdFrom?: AssetCreatedFrom;
} & Omit<ContainerSettings, 'container'>;

type UpdateEventSource = PIXI.utils.EventEmitter & {
    get name(): string;
};

export type CopyPasteData = Record<string, any> & {
    _templateReferenceMap: Record<string, string>;
};

export interface CopyPasteStyle extends Pick<SerializableMixinInterface, 'save'> {
    get stylePropertiesToCopy(): string[];
    copyStyleProperties(): Record<string, any>;
    pasteStyleProperties(settings: CopyPasteData): void;
    buildTemplateReferenceMap(): AssetTemplateReferenceMap;
}

/**
 * This BaseAsset class is the base class for MediaAsset and TextAsset, ensuring a consistent API.  This is an abstract
 * base class so methods will throw exceptions if not overwritten.
 */
export class BaseAsset extends Container implements Asset, CopyPasteStyle {
    override _classType = 'BaseAsset';

    #processing = false;

    // isFinalTopLevel defines whether or not this object is the final, top object in the hierarchy which needs to be updated
    isFinalTopLevel = false;

    // evaluation mode - are we building or playing back?
    _drawEvaluationMode: AssetMode = AssetMode.BUILD;

    // _autoDraw determines whether or not we immediately want to process changes
    _autoDraw = false;

    // _requiresRedraw determines whether or not the object has changed and needs to be updated
    _requiresRedraw = true;

    // updateParentOnRedraw determines whether or not changes on this object will require the parent to be updated as well
    updateParentOnRedraw = false;

    // Similar to updateParentOnRedraw, but climbs the luminaryParent instead of the parent hierarchy
    updateLuminaryParentOnRedraw = false;

    // the source object we want to emit our update events from
    updateEventSource: UpdateEventSource = this;

    @BooleanAttribute({ serialize: false })
    accessor displayBounds = false;
    @ColorAttribute({ serialize: false })
    accessor boundsColor = rgb2int([6, 180, 0]);
    @BooleanAttribute({ serialize: false })
    accessor displayMaxBounds = false;
    @ColorAttribute({ serialize: false })
    accessor maxBoundsColor = rgb2int([246, 70, 70]);
    @BooleanAttribute({ serialize: false })
    accessor displayAnchor = false;
    @ColorAttribute({ serialize: false })
    accessor anchorColor = rgb2int([88, 70, 246]);

    // allows assets from a remote source to be loaded
    readonly #fetchResources: FetchResources;

    protected sentCanPlay = false;

    /**
     * Determines whether or not this object is controlling the position of its child(ren). If it is then we can skip
     * certain steps such as setPosition.
     */
    controlsChildPosition = false;

    /**
     * Specifies if the asset has any user editable properties
     */
    #isEditable = true;

    /**
     * Specifies if the asset has any user editable colors
     */
    #isColorEditable = false;

    /**
     * The transformation is in control of serializing and deserializing the transformation of a PIXI container and
     * propagates these changes to the container through `applyTransformation`.
     */
    transformation: Transformation = new Transformation({
        parent: this,
        redrawParentOnUpdate: true,
    });

    childrenRequestingRedraw = [];

    // allows us to define when the intro animation for this asset begins.
    #introAnimationOffset = 0;

    // allows us to define when the outro animation for this asset begins.
    #outroAnimationOffset = 0;

    #boundsWithoutAnimation: Rectangle = Rectangle.EMPTY;

    _enableBoundsWithoutAnimationForSubtree = false;

    /**
     * The filters that are applied to this asset. These are the luminary filters, not the pixi filters.
     * The pixi filters applied to the container are in the pixi `filters` property.
     */
    #filters: Filter[] = [];

    /**
     * These animation behaviours are only available for assets which offer animation.
     * Currently, it's assets that extend DynamicStylingMixin and TextAssets
     */
    #animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_NEVER;
    #animationAnchorsToRemove: RemoveAnchoredPoints = Config.END_ANIMATION_CURVE_ANCHOR;

    morphParameters: MorphParameters = {
        startPoint: 0,
        interpolation: Config.LINEAR_INTERPOLATION_PRESET,
    };

    /**
     * @param container - the container used to parent the asset
     * @param settings - configuration settings
     */
    constructor(container: any, settings: BaseAssetSettings) {
        if (settings.name) {
            assertAssetName(settings.name);
        } else {
            settings = { ...settings, name: BaseAsset.name };
        }

        super({ container, ...settings });

        // Since we don't want to serialize these properties we can't use our load method and thus have to
        // load them by hand. Once we have Attributes we could define these as writable = false.
        if (settings.hasOwnProperty('isFinalTopLevel')) {
            this.isFinalTopLevel = settings.isFinalTopLevel;
        }
        if (settings.hasOwnProperty('autoDraw')) {
            this._autoDraw = settings.autoDraw;
        }
        if (settings.hasOwnProperty('requiresRedraw')) {
            this._requiresRedraw = settings.requiresRedraw;
        }
        if (settings.hasOwnProperty('updateParentOnRedraw')) {
            this.updateParentOnRedraw = settings.updateParentOnRedraw;
        }
        if (settings.hasOwnProperty('updateLuminaryParentOnRedraw')) {
            if (this.updateParentOnRedraw) {
                // Using both systems would be a performance anti-pattern, because it would repeat many updates.
                throw new Error(
                    'This system only supports one of updateLuminaryParentOnRedraw or updateParentOnRedraw',
                );
            }
            this.updateLuminaryParentOnRedraw = settings.updateLuminaryParentOnRedraw;
        }
        if (settings.hasOwnProperty('updateEventSource')) {
            if (typeof settings.updateEventSource.emit !== 'function') {
                throw Error(`Invalid event emitter ${settings.updateEventSource}`);
            }
            this.updateEventSource = settings.updateEventSource;
        }
        if (settings.hasOwnProperty('enableBoundsWithoutAnimationForSubtree')) {
            this._enableBoundsWithoutAnimationForSubtree =
                settings.enableBoundsWithoutAnimationForSubtree;
        }

        this.#fetchResources = settings.fetchResources;
    }

    get #displaysHelpers() {
        return this.displayBounds || this.displayMaxBounds || this.displayAnchor;
    }

    /**
     * @internal
     */
    _getAllBaseAssetsWithDisplayedHelpers(): BaseAsset[] {
        const assets: BaseAsset[] = [];
        if (this.#displaysHelpers) {
            assets.push(this);
        }
        for (const child of this._childAssets) {
            if (typeof child._getAllBaseAssetsWithDisplayedHelpers === 'function') {
                assets.push(...child._getAllBaseAssetsWithDisplayedHelpers());
            }
        }
        return assets;
    }

    get _childAssets(): BaseAsset[] {
        return this._pixiContainer.children.filter((child) => {
            return child instanceof BaseAsset;
        }) as any as BaseAsset[];
    }

    _createHelper(type = 'bounds', colorAttribute = 'boundsColor') {
        const settings = {
            asset: this,
            strokeColor: this[colorAttribute],
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            name: `${this.name}_${type}_helper`,
        };
        let helper;
        switch (type) {
            case 'bounds':
                helper = new AssetBoundsDisplay(settings);
                break;
            case 'maxBounds':
                helper = new AssetMaxBoundsDisplay(settings);
                break;
            case 'anchor':
                helper = new AssetAnchorDisplay(settings);
                break;
            default:
                throw Error(`Unknown helper of type ${type}`);
        }
        helper.requiresUpdate = true;
        return helper;
    }

    createHelpers() {
        const helpers = [];
        if (this.displayBounds) {
            helpers.push(this._createHelper('bounds', 'boundsColor'));
        }
        if (this.displayMaxBounds) {
            helpers.push(this._createHelper('maxBounds', 'maxBoundsColor'));
        }
        if (this.displayAnchor) {
            helpers.push(this._createHelper('anchor', 'anchorColor'));
        }
        return helpers;
    }

    /**
     * Returns the absolute start time of the asset relative in the global timeline. This can help us determine when the
     * asset is playing or visible.
     */
    get absoluteStartTime(): number {
        const timeline = this._luminaryParent._timeline as any;
        return timeline.startTimeRelativeToAncestor(this.root._timeline);
    }

    /**
     * Returns the absolute end time of the asset relative in the global timeline. This can help us determine when the
     * asset is playing or visible.
     */
    get absoluteEndTime(): number {
        const timeline = this._luminaryParent._timeline as any;
        return timeline.startTimeRelativeToAncestor(this.root._timeline) + timeline.duration;
    }

    /**
     * Flag to indicate if asset loading is enabled. Note this cannot be changed
     * after instantiation.
     */
    get fetchResources(): FetchResources {
        return this.#fetchResources;
    }

    _propagatePlay(): void {
        // nothing by default
    }

    _propagatePause(): void {
        // nothing by default
    }

    get failedToLoad(): boolean {
        // override
        throw Error('getter failedToLoad should be overridden');
    }

    get isLoaded(): boolean {
        // override
        throw Error(`${this.classType}: getter isLoaded should be overridden`);
    }

    get isDoneTryingToLoad(): boolean {
        return this.isLoaded || this.failedToLoad;
    }

    get canPlay(): boolean {
        if (!this.shouldLoad) {
            return true;
        }
        return this.isDoneTryingToLoad;
    }

    get approximateMaxBounds() {
        return this._pixiContainer.getBounds(true);
    }

    isMorphMatch(_other: BaseAsset): boolean {
        return false;
    }

    get morphExcludedProperties(): MorphableProperty[] {
        return [];
    }

    get boundsWithoutAnimation() {
        const topParent = this._getTopLevelParent();
        if (!topParent._enableBoundsWithoutAnimationForSubtree) {
            throw new Error(`boundsWithoutAnimation is not enabled for ${this.name}`);
        }
        return this.#boundsWithoutAnimation;
    }

    get _luminaryParent(): SceneGroup | Slide | Subslide {
        if (this.destroyed) {
            return null;
        }
        // I know this implementation looks confusing, but it's how luminary is organized right
        // now, cleaning this up would involve a larger luminary refactor, which we want to do at
        // some point.
        if (this.container.classType === 'Subslide') {
            // Subslides are the container for their assets
            return this.container as Subslide;
        }
        if (Config.isScene(this.container.slide)) {
            // e.g. handles the slideScene container and the SceneWatermarkSettings container
            return this.container.slide;
        }
        if ((this.container as unknown as Slide) === this.slide) {
            // Sometimes, usually in tests, we use the Slide as the container directly
            return this.container as unknown as Slide;
        }
        if (
            this.container.name === 'assetsWorkspace' &&
            this.container.container &&
            Config.isSceneGroup(this.container.container)
        ) {
            // The assets workspace on the scene group
            return this.container.container;
        }
        if (
            this.container.name === 'decorationsWorkspace' &&
            this.container.container.container &&
            Config.isSceneGroup(this.container.container.container)
        ) {
            // The decoration workspace where the decorations (example: background gradient) live in a CaptionsManager.
            return this.container.container.container as SceneGroup;
        }
        if (Config.isSceneGroup(this.container)) {
            return this.container;
        }
        const containersToForward = [
            'TransitionManager',
            'TransitionContainer',
            'AssetGroup',
            'CaptionsManager',
            'CompositingAsset',
        ];
        if (containersToForward.includes(this.container.classType)) {
            return this.container._luminaryParent;
        }
        throw new Error(`Could not find luminary parent for ${this.name}:${this.classType}`);
    }

    /**
     * Requests that this asset buffers some data at the given time.
     * Some examples are buffering video and caching textures.
     */
    async _bufferAtTime(_time: number, _bufferingMode: BufferingMode) {
        // by default do nothing
    }

    /**
     * The asset's mask.
     * This is the PIXI property extended for our update loop.
     * See PIXI documentation for more details. https://pixijs.download/dev/docs/PIXI.Container.html#mask
     */
    override set mask(mask: PIXI.Container | PIXI.MaskData | null) {
        /**
         * override the getter & setter for the "mask" property so we can trigger `requiresRedraw`
         * when it's changed
         */
        if (mask === this.mask) {
            return;
        }
        super.mask = mask;
        this.requiresRedraw = true;
    }

    override get mask() {
        return this._pixiContainer._mask;
    }

    /**
     * Add a brand new mask of type maskType to the BaseAsset
     */
    setMask<T extends MaskType>(maskType: T, settings): GetMaskType<T> {
        const mask = this._createMask({
            ...settings,
            classType: maskType,
        });
        this.mask = mask;
        return mask as GetMaskType<T>;
    }

    /**
     * Returns true if the asset's position is controlled by it's parent asset
     */
    get isPositionControlledByParent(): boolean {
        const asset = this.getParent();
        if (!asset || !Config.isAsset(asset)) {
            return false;
        }
        return asset.controlsChildPosition;
    }

    /**
     * Returns the asset or a parent asset of the asset that can be freely positioned
     */
    getFreelyPositionedParent() {
        let freeAsset: BaseAsset = this;
        while (freeAsset.isPositionControlledByParent) {
            freeAsset = freeAsset.getParent() as BaseAsset;
        }
        return freeAsset;
    }

    /**
     *
     * It returns a a list of OpaqueAsset[] (after .save())
     */
    saveWithNestedAssets(): OpaqueAsset[] {
        return [this.save()];
    }

    resetTransformation() {
        this._pixiContainer.x = 0;
        this._pixiContainer.y = 0;
        this._pixiContainer.rotation = 0;
    }

    /**
     * Sets the transformation of the asset, while ensuring that the asset stays within the bounds of the scene.
     */
    setTransformationPositionWithinSceneBounds(x: number, y: number) {
        // The transformation represents the static positioning of an asset, but we want to clamp the position based
        // on its current bounds. So we determine the change in the transformation position and then ensure that the change
        // doesn't cause the current bounds to go beyond the scene bounds.
        const bounds = this._pixiContainer.getBounds();
        const xShift = x - this.transformation.position.x;
        const yShift = y - this.transformation.position.y;
        const minXShift = 0 - bounds.x;
        const maxXShift = this.root.referenceResolution.width - bounds.width - bounds.x;
        const minYShift = 0 - bounds.y;
        const maxYShift = this.root.referenceResolution.height - bounds.height - bounds.y;
        x = this.transformation.position.x + Math.min(maxXShift, Math.max(minXShift, xShift));
        y = this.transformation.position.y + Math.min(maxYShift, Math.max(minYShift, yShift));

        this.transformation.position.x = x;
        this.transformation.position.y = y;
    }

    /**
     * Set the container position based on the transformation
     *
     * @param offsetX - additional offset to add to the x position
     * @param offsetY - additional offset to add to the y position
     * @param offsetRotation - additional offset to add to rotation
     */
    applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        const bounds = this._pixiContainer.getBounds();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            bounds.width,
            bounds.height,
        );

        // apply
        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    // Override the getter/setter for Container.animationConfig to hookup to update flow
    override get animationConfig() {
        return super.animationConfig;
    }

    override set animationConfig(data) {
        if (isEqual(this.animationConfig, data)) {
            return;
        }
        super.animationConfig = data;
        this.requiresRedraw = true;
    }

    /**
     * A flag which determines if this asset should be editable by the end user.
     *
     * Editable in this context means making direct changes to the asset in any way – changing it's colour, text value,
     * sourceData, etc. If `isEditable = false` this asset shouldn't have controls visible to the user.
     *
     * We _do_ expect this asset to be indirectly changed or changed via bulk actions. For example, modifying the
     * template colour palette or swapping colours may modify this asset.
     */
    get isEditable(): boolean {
        return this.#isEditable;
    }

    set isEditable(value: boolean) {
        if (this.isEditable === value) {
            return;
        }
        this.#isEditable = value;
        this.requiresRedraw = true;
    }

    /**
     * A flag which determines if color related properties of this asset should be editable by the end user.
     * if `isColorEditable = false` this asset shouldn't have color related controls visible to the user.
     */
    get isColorEditable(): boolean {
        return this.#isColorEditable;
    }

    set isColorEditable(value: boolean) {
        if (this.isColorEditable === value) {
            return;
        }
        this.#isColorEditable = value;
        this.requiresRedraw = true;
    }

    get visibleWithUpdate(): boolean {
        return this.visible;
    }

    /**
     * Updates PIXI `visible` property and triggers luminary redraw. This should be used instead of Pixi's visible
     * property
     */
    set visibleWithUpdate(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this._pixiContainer.visible = value;
        this.requiresRedraw = true;
    }

    /**
     * Returns the autoDraw value. If True, then this Object will immediately process any changes set by requiresRedraw.
     */
    get autoDraw(): boolean {
        return this._autoDraw;
    }

    /**
     * Set autoDraw on this asset and all of its children top-down. When autoDraw is set to true all property changes
     * will automatically be processed.
     */
    set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        const topParent = this._getTopLevelParent();
        if (!topParent) {
            return;
        }
        topParent._setAutoDrawForAllChildren(value);
        if (value) {
            if (this._drawEvaluationMode === AssetMode.BUILD) {
                this._checkAndFixPropertiesRecur();
            }
            this._buildAndDrawRecur();
        }
    }

    override get name() {
        return super.name;
    }

    override set name(name: string) {
        if (name === this.name) {
            return;
        }

        assertAssetName(name);
        const oldName = this.name;
        super.name = name;
        this.#updateTextPositionPresetNames(oldName, name);

        this.requiresRedraw = true;
    }

    #updateTextPositionPresetNames(oldName: string, newName: string) {
        // Assets not used in a traditional hierarchy, e.g. caption decorations, won't have access to a slide, nor
        // do they care about text position presets.
        if (!this.slide) {
            return;
        }

        const presets = this.slide.textPositionPresets;

        if (!presets) {
            return;
        }

        for (const presetName of Object.keys(presets)) {
            for (const preset of presets[presetName]) {
                if (preset.name === oldName) {
                    preset.name = newName;
                }
            }
        }
    }

    /**
     * Emits the assetCanPlay event indicating that the given asset can be played
     * back even though it might not be loaded yet. This is to ensure we don't
     * lock up workspace playback when a given asset takes longer then expected
     * to load.
     */
    _emitAssetCanPlay() {
        if (!this.sentCanPlay) {
            this.sentCanPlay = true;
            this.emit('assetCanPlay');
            if (this._luminaryParent) {
                this._luminaryParent.emit('assetCanPlay');
            }
        }
    }

    /**
     * Returns the top level parent. This will be the workScene for each Slide which contains all our PIXI renderable
     * components.
     * @protected
     */
    _getTopLevelParent(): BaseAsset {
        if (this.isFinalTopLevel) {
            return this;
        }
        // it's possible that our object isn't parent of any hierarchy just yet
        if (!this.parent) {
            return;
        }
        // it's possible that our parent isn't inherited from the BaseAsset
        if (!(this.parent instanceof BaseAsset)) {
            return;
        }
        return this.parent._getTopLevelParent();
    }

    /**
     * Recursively sets autoDraw for itself and all children
     * @protected
     */
    _setAutoDrawForAllChildren(value: boolean) {
        this._autoDraw = value;
        this._childAssets.forEach((asset) => {
            asset._setAutoDrawForAllChildren(value);
        });
    }

    /**
     * Adds one or more children to the container.
     *
     * Multiple items can be added like so: `myContainer.addChild(thingOne, thingTwo, thingThree)`
     *
     * @returns  The first child that was added.
     */
    _addAsset(asset: BaseAsset) {
        this._addAssetAt(asset, this._pixiContainer.children.length);
        return asset;
    }

    /**
     * Adds a child to the container at a specified index. If the index is out of bounds an error will be thrown
     */
    _addAssetAt(asset: BaseAsset, index: number) {
        // unparent child if necessary
        if (Config.isAsset(asset)) {
            const parent = asset.getParent();
            if (parent) {
                parent.removeChild(asset);
            }
        } else {
            if (asset.parent) {
                asset.parent.removeChild(asset);
            }
        }

        // parent child
        const targetIndex = this.childOrderConstraint(asset, index);
        if (targetIndex !== -1) {
            this._pixiContainer.addChildAt(asset._pixiContainer, targetIndex);

            asset.autoDraw = this.autoDraw;
            asset.requiresRedraw = true;

            // Register asset with Luminary for O(1) lookups
            // Safely handle cases where asset is not in a full Luminary hierarchy (e.g., tests)
            try {
                const luminaryParent = asset._luminaryParent;
                if (luminaryParent && '_luminary' in luminaryParent) {
                    (luminaryParent as any)._luminary._registerAsset(asset);
                }
            } catch (e) {
                // Asset not in Luminary hierarchy (e.g., unit tests), skip registration
            }
        }
    }

    /**
     * Extend the PIXI `removeChild` class to hookup to our update flow
     */
    override removeChild(...child) {
        return super.removeChild(...child);

        // TODO: I think this breaks videos by causing media assets to be removed from slides
        // NOTE: we ONLY want to require a redraw if we're removing a child which is a Luminary specified object
        // We don't want to require a redraw when removing a sprite for example. So we filter that by "autoDraw"
        // which works but is a bit obtuse?
        // if (child && 'autoDraw' in child) {
        //     this.requiresRedraw = true;
        // }
    }

    /**
     * Move the specified object to the specified target index.
     *
     * @param object
     */
    moveChild(object, index: number): boolean {
        const targetIndex = this.childOrderConstraint(object, index);
        if (targetIndex !== -1) {
            this.setChildIndex(object, targetIndex);
            this.requiresRedraw = true;
            return true;
        }
        return false;
    }

    /**
     * Check the desired target index against constraints an object's position within the children array.
     * @returns a safe index to add the child.
     */
    childOrderConstraint(object, targetIndex: number): number {
        if (targetIndex < 0) {
            return 0;
        }
        if (targetIndex > this._pixiContainer.children.length) {
            return this._pixiContainer.children.length;
        }
        return targetIndex;
    }

    /**
     * Check if this asset intersects with any target assets.
     */
    intersectsAny(targetAssets: BaseAsset[]): boolean {
        const sourceBounds = this._pixiContainer.getBounds();
        const sourceLeft = Math.floor(sourceBounds.x);
        const sourceRight = Math.ceil(sourceBounds.x + sourceBounds.width);
        const sourceTop = Math.floor(sourceBounds.y);
        const sourceBottom = Math.ceil(sourceBounds.y + sourceBounds.height);
        for (const targetAsset of targetAssets) {
            const targetBounds = targetAsset._pixiContainer.getBounds();
            if (
                targetBounds.contains(sourceLeft, sourceTop) ||
                targetBounds.contains(sourceLeft, sourceBottom) ||
                targetBounds.contains(sourceRight, sourceTop) ||
                targetBounds.contains(sourceRight, sourceBottom)
            ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Determine if this asset or any of its children require to be redrawn
     */
    get recursiveRequiresRedraw(): boolean {
        if (this.requiresRedraw) {
            return true;
        }
        return this._childAssets.some((asset) => {
            if (typeof asset.requiresRedraw !== 'boolean') {
                return false;
            }
            if (typeof asset.recursiveRequiresRedraw !== 'function') {
                return asset.requiresRedraw;
            }
            return asset.recursiveRequiresRedraw;
        });
    }

    /**
     * Returns whether or not the object has changes that need to be drawn.
     */
    get requiresRedraw(): boolean {
        return this._requiresRedraw;
    }

    /**
     * Set whether or not the given changes requires us to redraw the object. If for example we change the color or size
     * of the object then we need to pass on a value of True. This will then redraw the object with the new color or size.
     * @param value
     */
    set requiresRedraw(value: boolean) {
        assertBoolean(value);

        this._requiresRedraw = value;

        if (value) {
            if (this._drawEvaluationMode === AssetMode.BUILD) {
                this._checkAndFixPropertiesRecur();
            }
            this._buildAndDrawRecur();
        }
    }

    childRequestingRedraw(child) {
        this.childrenRequestingRedraw.push(child);
        this.requiresRedraw = true;
        this.childrenRequestingRedraw = [];
    }

    /**
     * Indicates if the asset should be loaded as a result of fetchResources setting.
     */
    get shouldLoad(): boolean {
        if (typeof this.fetchResources !== 'boolean') {
            throw new Error(
                `${this.classType} needs to override shouldLoad to handle fetchResources as an object`,
            );
        }
        return this.fetchResources;
    }

    /**
     * Allows an asset to specify conditions which will prevent a build and draw from happening.
     * If loading is disabled for an asset, then there's nothing to build nor draw.
     */
    get skipBuildAndDraw(): boolean {
        return !this.shouldLoad;
    }

    /**
     * The building logic stub. To be overridden by the child class. If anything needs to be build or updated, this is
     * the place to do it.
     */
    protected _build() {
        // should be defined within the child class if we want to build something
    }

    /**
     * The draw logic stub. To be overridden by the child class. If anything needs to be drawn, then this is the place
     * to do it.
     */
    protected _draw() {
        // should be defined within the child class if we want to draw something
    }

    /**
     * Runs for the current state of the BaseAsset, correcting or updating any
     * properties as necessary. It's important that this method does not throw errors, but
     * rather fixes the properties in-line.
     */
    _checkAndFixProperties() {
        const inSubscene = !!this.subscene || this.classType === 'Subslide';
        if (
            inSubscene &&
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_NEVER &&
            this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE).length === 0
        ) {
            this.animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING;
        } else if (
            !inSubscene &&
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
        ) {
            this.animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_NEVER;
        }
    }

    _checkAndFixPropertiesRecur(traverseChildren = true) {
        // if we're drawing (aka the #processing flag) we don't need to recompute properties and definitely
        // don't want to start drawing again – return early!
        if (this.#processing) {
            return;
        }

        // no longer build if object is destroyed
        if (this.destroyed) {
            return;
        }

        // Make sure the object is part of a hierarchy (scene)
        // If it hasn't yet been attached to a parent then we don't need to draw and/or check and fix properties yet.
        if (!this.parent && (!this.isFinalTopLevel || this.updateParentOnRedraw)) {
            return;
        }

        // activate the "processing" (aka building/drawing) flag so we don't reevaluate when updating properties in
        // the following `_checkAndFixProperties()` call or when calling `_fixBuildDraw()` on children
        this.#processing = true;

        if (traverseChildren) {
            this._childAssets.forEach((asset) => {
                asset._checkAndFixPropertiesRecur();
            });
        }

        this._checkAndFixProperties();

        if (this.parent && this.parent instanceof BaseAsset) {
            this.parent._checkAndFixPropertiesRecur(false);
        }

        this.#processing = false;
    }

    _buildAndDrawRecur() {
        if (!this.autoDraw) {
            return;
        }

        // if we're drawing (aka the #processing flag) we don't need to recompute properties and definitely
        // don't want to start drawing again – return early!
        if (this.#processing) {
            return;
        }

        // no longer build if object is destroyed
        if (this.destroyed) {
            return;
        }

        // Make sure the object is part of a hierarchy (scene)
        // If it hasn't yet been attached to a parent then we don't need to draw and/or check and fix properties yet.
        if (!this.parent && (!this.isFinalTopLevel || this.updateParentOnRedraw)) {
            return;
        }

        // activate the "processing" (aka building/drawing) flag so we don't reevaluate when updating properties in
        // the following `_checkAndFixProperties()` call or when calling `_fixBuildDraw()` on children
        this.#processing = true;

        this._childAssets.forEach((asset) => {
            asset._buildAndDrawRecur();
        });

        if (!this.requiresRedraw) {
            this.#processing = false;
            return;
        }

        try {
            // process local changes
            if (!this.skipBuildAndDraw) {
                if (this._drawEvaluationMode === AssetMode.BUILD) {
                    this._build();

                    if (this.isFinalTopLevel && this._enableBoundsWithoutAnimationForSubtree) {
                        // once the top of the hierarchy has been built, we can cache the bounds
                        this._cacheBoundsAfterBuildRecursively();
                    }
                }
                this._draw();
            }
        } finally {
            // all local changes have now been processed
            this.requiresRedraw = false;
            this.#processing = false;
        }

        if (this._drawEvaluationMode === AssetMode.BUILD) {
            if (this.updateLuminaryParentOnRedraw) {
                if (!this._luminaryParent) {
                    console.error(
                        'updateLuminaryParentOnRedraw is set to true, but we have no Luminary parent.',
                    );
                }
                this._luminaryParent.requiresUpdate = true;
            } else if (this.updateParentOnRedraw) {
                if (!this.parent) {
                    console.warn(
                        `Unable to redraw the parent for ${this.name} since parent is ${this.parent}.`,
                    );
                    return;
                }
                if (!(this.parent instanceof BaseAsset)) {
                    return;
                }
                // ask the parent asset to redraw
                this.parent.childRequestingRedraw(this);
            } else {
                // All the dependent assets have been drawn, inform the scene we're finished updating
                this._updateEvent();
            }
        }
    }

    /**
     * This method gets called once everything has been updated.
     * @protected
     */
    _updateEvent() {
        this.updateEventSource.emit('childUpdated', this.name);
    }

    /**
     * This recursively caches the bounds of all children. This is intended to be used immediately
     * after a build and will cache the latest bounds for each asset in the whole subtree.
     *
     * The first call should be made at the top of a hierarchy with skipUpdate set to false. That
     * way the bounds for the whole subtree are updated once. Then each recursive call will not
     * again re-calculate the bounds.
     * @internal
     */
    _cacheBoundsAfterBuildRecursively(opts = { skipUpdate: false }) {
        this._pixiContainer.getBounds(opts.skipUpdate, this.#boundsWithoutAnimation);
        this._childAssets.forEach((asset) => {
            if (asset.isFinalTopLevel) {
                // There are some nested trees. E.g. SceneGroup & Scene. We don't need to drill
                // down into a nested hierarchy from the outer one because the nested one will
                // also do its own update if necessary.
                return;
            }
            asset._cacheBoundsAfterBuildRecursively({ skipUpdate: true });
        });
    }

    /**
     * Animate or progress to a certain value. This will update any driven properties and set any requiresRedraw to True
     * if changed
     * @internal
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    _seek(progress: number, seekMode: SeekMode) {
        if (!this.animationManager) {
            return;
        }
        // limit our computations by only evaluating values that will trigger a change
        if (progress >= 0 && progress <= 1) {
            this.animationManager.percentage = progress;
        }
        if (this.mask instanceof PixiContainer) {
            const maskBounds = this.mask.getBounds();
            // Ensure the mask isn't offscreen. Otherwise, we won't see its effect, and we'll have to hide the masked
            // object ourselves.
            if (maskBounds.x + maskBounds.width < 0 || maskBounds.y + maskBounds.height < 0) {
                this._pixiContainer.visible = false;
            } else {
                this._pixiContainer.visible = true;
            }
        }
    }

    /**
     * Calls _seek for itself and all of its children
     * @internal
     */
    _recursiveSeek(progress: number, seekMode: SeekMode) {
        this._childAssets.forEach((asset) => {
            asset._recursiveSeek(progress, seekMode);
        });
        // update all driven properties
        this._seek(progress, seekMode);
    }

    /**
     * Applies intro animations to the asset. This method should be called by derived classes after `applyAnimation()`
     * in their `_build()` implementation.
     */
    applyIntroAnimations() {
        const isOnFirstSubscene = this.subscene && this.subscene.index === 0;
        const isNotOnSubsceneLevel = !this.subscene;
        const hasIntroAnimation = this.animationManager.hasAnchors(
            Config.START_ANIMATION_CURVE_ANCHOR,
        );
        const shouldApplyIntroAnimationOffset =
            (isOnFirstSubscene || isNotOnSubsceneLevel) && hasIntroAnimation;
        if (shouldApplyIntroAnimationOffset) {
            const timeOffset = this.introAnimationOffset / this.container.duration;
            this.animationManager.moveAllGroups(timeOffset, 0, Config.START_ANIMATION_CURVE_ANCHOR);
        }
    }

    /**
     * Applies outro animations to the asset. This method should be called by derived classes after `applyAnimation()`
     * in their `_build()` implementation.
     */
    applyOutroAnimations() {
        const shouldApplyOutroAnimationOffset = this.animationManager.hasAnchors(
            Config.END_ANIMATION_CURVE_ANCHOR,
        );
        if (!shouldApplyOutroAnimationOffset) {
            return;
        }

        const timeOffset = this.outroAnimationOffset / this.container.duration;
        this.animationManager.moveAllGroups(-timeOffset, 0, Config.END_ANIMATION_CURVE_ANCHOR);
    }

    get durationAnimatedIn(): number {
        if (
            this.animationManager.hasAnchors(Config.END_ANIMATION_CURVE_ANCHOR) &&
            this.animationManager.hasAnchors(Config.START_ANIMATION_CURVE_ANCHOR)
        ) {
            return this.animationManager.outroEnd - this.animationManager.introStart;
        }
        if (this.animationManager.hasAnchors(Config.END_ANIMATION_CURVE_ANCHOR)) {
            return this.animationManager.outroEnd;
        }
        if (this.animationManager.hasAnchors(Config.START_ANIMATION_CURVE_ANCHOR)) {
            const endingTime = Math.max(this.container.duration, this.animationManager.introEnd);
            return endingTime - this.animationManager.introStart;
        }
        return this.container.duration;
    }
    /**
     * Animate or evaluate the object and its children to the given progress
     */
    seek(progress: number, seekMode: SeekMode) {
        this._batchRedraw(() => {
            // set draw evaluation mode to EVAL mode so we only evaluate our existing hierarchy properties
            // and don't build any new elements.
            this._setDrawEvaluationMode(AssetMode.EVAL);
            this._recursiveSeek(progress, seekMode);
        });

        // reset draw evaluation mode back to BUILD mode
        this._setDrawEvaluationMode(AssetMode.BUILD);
    }

    /**
     * Set the internal drawing mode to either ASSET_EVAL_MODE or ASSET_BUILD_MODE.
     */
    _setDrawEvaluationMode(value: AssetMode) {
        assertInEnum(value, AssetMode);
        this._drawEvaluationMode = value;
        this._childAssets.forEach((asset) => {
            asset._setDrawEvaluationMode(value);
        });
    }

    get introAnimationOffset() {
        return this.#introAnimationOffset;
    }

    set introAnimationOffset(value) {
        if (this.introAnimationOffset === value) {
            return;
        }
        this.#introAnimationOffset = value;
        this.requiresRedraw = true;
    }

    get outroAnimationOffset() {
        return this.#outroAnimationOffset;
    }

    set outroAnimationOffset(value) {
        if (this.outroAnimationOffset === value) {
            return;
        }
        this.#outroAnimationOffset = value;
        this.requiresRedraw = true;
    }

    /**
     * The introAnimationOffset is used to calculate the actual time that an asset will start its intro animation.
     * For example, if the asset is in a group, the asset's intro animation could be staggered by the other
     * assets in the group.
     *
     * appliedIntroAnimationOffset is the actual time that the asset will start its intro animation.
     */
    get appliedIntroAnimationOffset() {
        if (!this.animationManager || !this.container) {
            return 0;
        }
        const { parentOffset, parentHasAnimation } = (() => {
            const assetGroupParent = this.getParentByType(Config.GROUP_ASSET_TYPE);
            if (!assetGroupParent || !assetGroupParent.hasObjectAnimation) {
                return { parentOffset: 0, parentHasAnimation: false };
            }
            return {
                parentOffset: assetGroupParent.introAnimationOffset,
                parentHasAnimation: true,
            };
        })();

        if (parentHasAnimation && parentOffset !== 0) {
            return parentOffset;
        }

        const xPoints = this.animationManager.groups.flatMap((group) => {
            return group.animCurves
                .flatMap((curve) => {
                    const points = curve.getEndPoints(Config.START_ANIMATION_CURVE_ANCHOR);
                    for (let i = 0; i < points.length; i++) {
                        const currentPoint = points[i];
                        const nextPoint = points[i + 1];
                        if (nextPoint && nextPoint.y !== currentPoint.y) {
                            return currentPoint.x;
                        }
                    }
                    return null;
                })
                .filter((x) => x !== null);
        });
        if (xPoints.length === 0) {
            return 0;
        }
        return Math.min(...xPoints) * this.container.duration;
    }

    /**
     * Similar to {@link appliedIntroAnimationOffset}, but for the outro animation.
     */
    get appliedOutroAnimationOffset() {
        if (!this.animationManager || !this.container) {
            return 0;
        }
        const { parentOffset, parentHasAnimation } = (() => {
            const assetGroupParent = this.getParentByType(Config.GROUP_ASSET_TYPE);
            if (!assetGroupParent || !assetGroupParent.hasObjectAnimation) {
                return { parentOffset: 0, parentHasAnimation: false };
            }
            return {
                parentOffset: assetGroupParent.outroAnimationOffset,
                parentHasAnimation: true,
            };
        })();

        if (parentHasAnimation && parentOffset !== 0) {
            return parentOffset;
        }

        const xPoints = this.animationManager.groups.flatMap((group) => {
            return group.animCurves
                .flatMap((curve) => {
                    const points = curve.getEndPoints(Config.END_ANIMATION_CURVE_ANCHOR);
                    for (let i = points.length - 1; i >= 0; i--) {
                        const currentPoint = points[i];
                        const prevPoint = points[i - 1];
                        if (prevPoint && prevPoint.y !== currentPoint.y) {
                            return currentPoint.x;
                        }
                    }
                    return null;
                })
                .filter((x) => x !== null);
        });

        if (xPoints.length === 0) {
            return 0;
        }
        return (1 - Math.max(...xPoints)) * this.container.duration;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.root.outputResolution.width / this.root.referenceResolution.width;
    }

    getBoundsInReferenceResolution(bounds: PIXI.Rectangle) {
        const newBounds = bounds.clone();
        newBounds.x /= this.referenceToOutputMultiplier;
        newBounds.y /= this.referenceToOutputMultiplier;
        newBounds.width /= this.referenceToOutputMultiplier;
        newBounds.height /= this.referenceToOutputMultiplier;
        return newBounds;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'animateOutBehaviour',
            'transformation',
            'filters',
            'isEditable',
            'isColorEditable',
            'introAnimationOffset',
            'outroAnimationOffset',
        ]);
    }

    /**
     * Custom mask load method used in load
     * NOTE: we don't add "mask" as a property to serialize here, but we do in classes that inherit BaseAsset
     * @protected
     */
    _maskCustomLoad(properties) {
        // properties is undefined when we saved out a mask property which wasn't set
        if (!properties) {
            return;
        }
        if (!(this.mask === null || this.mask === undefined)) {
            return;
        }
        this.mask = this._createMask(properties);
    }

    /**
     * Custom mask save method used in save
     * NOTE: we don't add "mask" as a property to serialize here, but we do in classes that inherit BaseAsset
     * @internal
     */
    _maskCustomSave(): object {
        if (!this.mask) {
            return null;
        }
        if (this.mask instanceof StencilMask) {
            return this.mask.save();
        } else {
            return null;
        }
    }

    /**
     * Custom visible load method used in load
     * NOTE: we need this because Pixi defines the visible property so we created our own to propagate updates
     * @internal
     */
    _visibleCustomLoad(value: boolean) {
        this.visibleWithUpdate = value;
    }

    _createMask<T extends MaskType>(properties: { classType: T }): GetMaskType<T> {
        let mask: CircleMask | PolygonMask;
        const settings = {
            name: this.name + '_mask',
            updateTarget: this,
        };
        // create the necessary mask
        switch (properties.classType) {
            case Config.CIRCLE_MASK_TYPE:
                mask = new CircleMask(settings);
                break;
            case Config.POLYGON_MASK_TYPE:
                mask = new PolygonMask(settings);
                break;
            default:
                throw Error(`Unknown mask type ${properties.classType}`);
        }
        mask.load(properties);
        return mask as GetMaskType<T>;
    }

    /**
     * Returns the container where filters are applied for the asset.
     */
    protected get _pixiFilterContainer(): PIXI.DisplayObject {
        return this._pixiContainer;
    }

    _filtersCustomSave() {
        if (!this.#filters) {
            return null;
        }
        return this.#filters.map((filter) => filter.save());
    }

    _filtersCustomLoad(data) {
        if (!data) return;

        data.forEach((config) => {
            // Only need to create the filter if it doesn't yet exist
            let filter = this.getFilterByName(config.name);
            if (!filter) {
                filter = this.addFilter(config.classType, config.name);
            }
            filter.load(config);
        });
    }

    /**
     * Add filter and triggers an update. Use this method instead of directly adding a filter to the filters array.
     * If providing a name, it needs to be unique among the filters already added to this asset.
     */
    addFilter(classType: 'BlurFilter', name?: string): BlurFilter;
    addFilter(classType: 'AdjustmentFilter', name?: string): AdjustmentFilter;
    addFilter(classType: 'ColorChannelMapFilter', name?: string): ColorChannelMapFilter;
    addFilter(classType: 'GreyscaleFilter', name?: string): GreyscaleFilter;
    addFilter(classType: 'DuoToneFilter', name?: string): DuoToneFilter;
    addFilter(classType: 'HslAdjustmentFilter', name?: string): HslAdjustmentFilter;
    addFilter(classType: 'OldFilmFilter', name?: string): OldFilmFilter;
    addFilter(classType: 'ColorGradingFilter', name?: string): ColorGradingFilter;
    addFilter(
        classType:
            | 'BlurFilter'
            | 'AdjustmentFilter'
            | 'ColorChannelMapFilter'
            | 'GreyscaleFilter'
            | 'DuoToneFilter'
            | 'HslAdjustmentFilter'
            | 'OldFilmFilter'
            | 'ColorGradingFilter',
        name?: string,
    ): Filter {
        let filter: Filter;
        const settings = {
            name,
            parent: this,
            redrawParentOnUpdate: true,
            fetchResources: this.#fetchResources,
        };

        if (!settings.name) {
            settings.name = `${classType}_${(this.#filters?.filter((f) => f.classType === classType).length || 0) + 1}`;
        }

        if (this.#filters && this.#filters.some((f) => f.name === settings.name)) {
            throw Error(`Name of filter should be unique`);
        }

        assertInArray(classType, Config.ALL_FILTERS);

        switch (classType) {
            case Config.BLUR_FILTER:
                filter = new BlurFilter(settings);
                break;
            case Config.ADJUSTMENT_FILTER:
                filter = new AdjustmentFilter(settings);
                break;
            case Config.COLOR_CHANNEL_MAP_FILTER:
                filter = new ColorChannelMapFilter(settings);
                break;
            case Config.GREYSCALE_FILTER:
                filter = new GreyscaleFilter(settings);
                break;
            case Config.DUO_TONE_FILTER:
                filter = new DuoToneFilter(settings);
                break;
            case Config.HSL_ADJUSTMENT_FILTER:
                filter = new HslAdjustmentFilter(settings);
                break;
            case Config.OLD_FILM_FILTER:
                filter = new OldFilmFilter(settings);
                break;
            case Config.COLOR_GRADING_FILTER:
                filter = new ColorGradingFilter(settings);
                break;
            default:
                throw Error(`Unable to create unknown filter ${classType}`);
        }

        if (!this._pixiFilterContainer.filters) {
            this._pixiFilterContainer.filters = [];
        }

        if (this.root.renderer) {
            filter.resolution = this.root.renderer.resolution;
        }

        this.#filters.push(filter);
        this._pixiFilterContainer.filters.push(filter.pixiFilter);
        this.requiresRedraw = true;
        return filter;
    }

    /**
     * Retrieves the list of luminary filters. Once we separate the pixi hierarchy from the luminary hierarchy, we
     * can remove this method and just use a getter for filters. (Currently PIXI defines the filters getter)
     */
    getFilters(): Filter[] {
        return this.#filters;
    }

    /**
     * Get filter by name
     */
    getFilterByName(name: string): Filter {
        return this.#filters?.find((filter) => filter.name === name);
    }

    /**
     * Remove filter by name
     */
    removeFilter(name: string) {
        const index = this.#filters.findIndex((filter) => filter.name === name);
        if (index === -1) {
            return;
        }
        const filter = this.#filters[index];
        const pixiIndex = this._pixiFilterContainer.filters.indexOf(filter.pixiFilter);

        this.#filters.splice(index, 1);
        if (pixiIndex !== -1) {
            this._pixiFilterContainer.filters.splice(pixiIndex, 1);
        }
        filter.destroy();
        this.requiresRedraw = true;
    }

    protected removeFilterIfNeutral(name: string) {
        const filter = this.getFilterByName(name);
        if (!filter) {
            return;
        }
        if (filter.isNeutral()) {
            this.removeFilter(name);
        }
    }

    /**
     * Bulk load properties with autoDraw disabled
     */
    override load(dataObj, settings: LoadSettings = { skipTemplateAttributes: false }) {
        this._batchRedraw(() => {
            super.load(dataObj, settings);
        });
        return this;
    }

    /**
     * Load all editable properties with autoDraw disabled.
     */
    override loadEditable(dataObj) {
        this._batchRedraw(() => {
            super.loadEditable(dataObj);
        });
        return this;
    }

    hasSuffix(value: string): boolean {
        const suffix = (() => {
            const match = this.name.match(ASSET_NAME_REGEX);
            if (!match) {
                return '';
            }
            return match[3] ?? '';
        })();

        return suffix.includes(value);
    }

    /**
     * Get all of the child attributes that are linked to a template with their associated reference value. This
     * includes dynamic styling and filters. We return an object where the attribute is the key and the reference
     * value is the value.
     */
    buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        const referenceMap: Record<string, string> = {};

        // get asset properties
        const templateAttributes = getAllTemplateAttributes(
            this,
            ValueMapMode.GET_REFERENCE_VALUES,
        );
        templateAttributes.forEach((attr) => {
            referenceMap[attr] = getTemplateValueAttribute(this, attr).replace('themeRef_', '');
        });
        // get filter properties
        if (Array.isArray(this.#filters)) {
            this.#filters.forEach((filter, index) => {
                const attrKeyPrefix = `filters.${index}`;
                const templateAttributes = getAllTemplateAttributes(
                    filter,
                    ValueMapMode.GET_REFERENCE_VALUES,
                );
                templateAttributes.forEach((attr) => {
                    const attrKey = `${attrKeyPrefix}.${attr}`;
                    const val = getTemplateValueAttribute(filter, attr);
                    referenceMap[attrKey] = val.replace('themeRef_', '');
                });
            });
        }

        return referenceMap;
    }

    hasTemplateReferenceValues(): boolean {
        return objectHasTemplateValues(this);
    }

    /**
     * This method can be overriden to define which part of an asset is a style property.
     */
    get stylePropertiesToCopy(): string[] {
        return [];
    }

    copyStyleProperties(): CopyPasteData {
        return getStylePropertiesToCopy(this);
    }

    /**
     * Validate template reference map and load settings in luminary.
     */
    pasteStyleProperties(settings: CopyPasteData) {
        const templateIndexErrorsList: string[] = [];

        findTemplateIndexErrors(
            this.template,
            '',
            settings._templateReferenceMap,
            templateIndexErrorsList,
        );

        if (templateIndexErrorsList.length > 0) {
            throw new Error(templateIndexErrorsList.join(' '));
        }

        this.load(settings);
    }

    get _animationAnchorsToRemove() {
        return this.#animationAnchorsToRemove;
    }

    set _animationAnchorsToRemove(value: RemoveAnchoredPoints) {
        if (this._animationAnchorsToRemove === value) {
            return;
        }
        assertInArray(value, Config.ANIMATION_CURVE_ANCHORS);
        if (
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS &&
            value === Config.END_ANIMATION_CURVE_ANCHOR
        ) {
            throw new Error(
                `${this.name}'s animate out behaviour does not allow removing the end anchor`,
            );
        }
        this.#animationAnchorsToRemove = value;
        this.requiresRedraw = true;
    }

    /**
     * Defines the allowed behaviour of all exit animations for this class.
     * If ALWAYS is set, then the asset will ensure no end anchor points are removed.
     * If WHEN_PRECEDING is set, then the asset will allow end anchor points to be removed
     * by calling removeOutAnimationIfAllowed
     */
    get animateOutBehaviour(): string {
        return this.#animateOutBehaviour;
    }

    set animateOutBehaviour(value: string) {
        if (this.animateOutBehaviour === value) {
            return;
        }
        assertInArray(value, Config.ANIMATE_OUT_BEHAVIOURS);
        this.#animateOutBehaviour = value;
        switch (this.#animateOutBehaviour) {
            case Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS:
                this.#animationAnchorsToRemove = Config.NONE_ANIMATION_CURVE_ANCHOR;
                break;
            case Config.ANIMATE_OUT_BEHAVIOUR_NEVER:
            case Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING:
                this.#animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
                break;
        }
        this.requiresRedraw = true;
    }

    override destroy(options?: DestroyOptions) {
        // Unregister from Luminary asset index before destroying
        // Safely handle cases where asset is not in a full Luminary hierarchy (e.g., tests)
        try {
            const luminaryParent = this._luminaryParent;
            if (luminaryParent && '_luminary' in luminaryParent) {
                (luminaryParent as any)._luminary._unregisterAsset(this.id);
            }
        } catch (e) {
            // Asset not in Luminary hierarchy, skip unregistration
        }

        super.destroy(options);
        this.#destroyFilters();
    }

    #destroyFilters() {
        this.#filters.forEach((filter) => {
            filter.destroy();
        });
        this.#filters = [];
        this._pixiFilterContainer.filters = null;
    }

    _batchRedraw(work: () => void) {
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;
        try {
            work();
        } finally {
            this.autoDraw = initAutoDraw;
        }
    }
}
